import React, { ReactNode, useMemo } from "react";
import * as R from "ramda";
import { useQuery } from "@apollo/client";
import { getGraphQLErrorCode } from "@app/provider/error/graphQLErrorCode";
import TestCardService from "@app/service/testCard";
import { NotificationContext } from "@app/provider";
import { TestDetailsRecord } from "@app/provider/types";
import { ResultCodes } from "@app/service/resultCodes";
import { useLocation, useParams } from "react-router-dom";
import { useDialog } from "@natera/platform/lib/hooks";
import { LoadingContext } from "@natera/platform/lib/components/context";
import TestNotFoundModal from "@app/components/testNotFoundDialog/testNotFound";
import { getTestDetailsWithBrowserTimezone } from "./utils/getTestDetailsWithBrowserTimezone";
import { getDescription } from "@app/provider/testData/utils";
import { useIntl } from "react-intl";
import { TestType } from "@app/provider/testData/types";
import { TestCardBuilder } from "@app/provider/testData/testCard/TestCardBuilder";
import { isConnectPortal, isOrganHealthPortal } from "@app/utils";

export type TestIndetifyer = {
  orderUidPublic: string;
  testUid: string;
};

export interface TestProviderController {
  isLoading: boolean;
  getTestDetails: () => TestDetailsRecord | undefined;
  getOrderUidPublic: () => string;
  getTestUid: () => string;
  refetchTest: () => Promise<TestDetailsRecord>;
}

export const Context = React.createContext<TestProviderController>({
  isLoading: false,
  getTestDetails: () => undefined,
  getOrderUidPublic: R.always(""),
  getTestUid: R.always(""),
  refetchTest: () => Promise.reject(),
});

Context.displayName = "TestDetailsRecordContext";

const TestProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const intl = useIntl();

  const location = useLocation();
  const testNotFoundDialog = useDialog(TestNotFoundModal);
  const { addNotification } = React.useContext(NotificationContext);

  const { orderUidPublic, testUid } = useParams<
    keyof TestIndetifyer
  >() as TestIndetifyer;

  const {
    data: testDetails,
    loading: isLoading,
    error: getTestCardDetailsError,
    refetch,
  } = useQuery<{
    getTestCardDetails: TestDetailsRecord;
  }>(TestCardService.getTestCardDetails(), {
    fetchPolicy: "network-only",
    variables: { orderUidPublic, testUid },
  });

  const isResultPdfPage = React.useMemo(() => {
    return location.pathname.includes("result-pdf");
  }, [location.pathname]);

  React.useEffect(() => {
    handleGetTestDetails();
  }, [testDetails]);

  React.useEffect(() => {
    handleError();
  }, [getTestCardDetailsError]);

  const handleError = () => {
    if (
      getGraphQLErrorCode(getTestCardDetailsError) ===
      ResultCodes.TEST_NOT_FOUND
    ) {
      testNotFoundDialog.open({});
    }

    if (getTestCardDetailsError?.networkError && !isResultPdfPage) {
      addNotification({
        type: "error",
      });
    }
  };

  const handleGetTestDetails = () => {
    try {
      const testData = testDetails?.getTestCardDetails;

      if (!testData) {
        throw new Error();
      }

      let description: string | undefined;

      // Use TestCardBuilder only for Latitude to not affect other tests for now, until we refactor the rest of the oncology tests to use class TestCardBuilder
      if (
        !testData.i18nKeys &&
        (isConnectPortal(testData.testType) ||
          isOrganHealthPortal(testData.testType) ||
          testData.testType === TestType.LATITUDE) //todo: change logic for oncology portal when the rest of the oncology tests (signatera, altera) are refactored to use class TestCardBuilder
      ) {
        const testCardBuilder = new TestCardBuilder();
        const testCardDescription = testCardBuilder.getTestCardDescription(
          testData,
          intl
        );
        description = testCardDescription;
      }

      // getDescription handles both rules-based (i18nKeys) and legacy cards.
      if (!description) {
        description = getDescription(intl, testData);
      }

      const testDataMutated = {
        ...testData,
        description: description,
      };

      return getTestDetailsWithBrowserTimezone(testDataMutated);
    } catch (error) {
      //
    }
  };

  const refetchTest = async () => {
    const testData = await refetch({ testUid, orderUidPublic });
    return testData.data.getTestCardDetails;
  };

  const testProviderController: TestProviderController = useMemo(
    () => ({
      isLoading,
      getTestDetails: handleGetTestDetails,
      getOrderUidPublic: R.always(orderUidPublic),
      getTestUid: R.always(testUid),
      refetchTest,
    }),
    [isLoading, handleGetTestDetails, orderUidPublic, testUid, refetchTest]
  );

  return (
    <Context.Provider value={testProviderController}>
      <LoadingContext isLoading={isLoading}>
        {testNotFoundDialog.getDialog()}
        {testDetails?.getTestCardDetails || isResultPdfPage ? children : <></>}
      </LoadingContext>
    </Context.Provider>
  );
};

export default TestProvider;
